import React from 'react'
import MigrationProgress from '../components/MigrationProgress'
import CrumplePopAccountDetails from '../components/CrumplePopAccountDetails'
import CrumplePopLoginPanel from '../components/CrumplePopSignin/CrumplePopLoginPanel'
import { fetchCustomer } from '../../common/js/bfx-customer-utility'
import { getStripeKey } from '../../common/js/stripe-utility'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { axiosGet, axiosPost } from '../../common/js/axios-utility'

import { CrumplePopSignupContext } from '../contexts/CrumplePopSignupContext'
import CrumplePopSignup from './CrumplePopSignin/CrumplePopSignup'
import CrumplePopSubscriptionMigration from './CrumplePopSubscriptionMigration'
import BorisSignIn from './BorisSignIn'
import MigratrionLoadingPage from './MigrationLoadingPage'
import CrumplePopHubDownload from './CrumplePopHubDownload'
import CrumplePopMigrationResults from './CrumplePopMigrationResults'

import StripeProvider from '../../common/components/payment-method/StripeProvider'
import SpinnerOverlay from '../../common/components/SpinnerOverlay'

import { errorToast } from '../../common/js/toast-utility'
import { getLoginCookie, getRawLoginCookie, setLoginCookie } from '../../common/js/login-cookie-utility'


export default function CrumplePopMigration() {
    const [ready, setReady] = React.useState(false);
    const customerInfo = useQuery({
        queryKey: ['cpop', 'customer'], 
        queryFn: async () => await fetchCustomer(), 
        refetchOnWindowFocus: false
    });
    const queryClient = useQueryClient()
    const [migrationState, setMigrationState] = React.useContext(CrumplePopSignupContext)

    const customer = customerInfo.data;

    const migrationSteps = [
        { name: 'login', label: '1. Sign In' },
        { name: 'signup,bfx-login,subscription', label: '2. Account Details' },
        { name: 'download', label: '3. App Download' }
    ];

    let content = <></>;

    React.useEffect(() => {
        const doInit = async () => {
            const cpopLogin = Cookies.get('cpop_login');
            const bfxLogin = getRawLoginCookie()
            //quick poll for migration process
            if (bfxLogin != null){
                //AxiosUtility fills in auth details. No need to add to request here.
                try{
                    const {data} = await axiosGet('/cpop/poll');
                    if (data && data.status != 'error'){
                        setMigrationState({...migrationState, page:'subscription', migrationInProgress: true});
                        setReady(true);
                        return;
                    }
                }catch(err){}
            }

            //check for existing Cpop login
            if (cpopLogin != null){
                await handleCpopLoginComplete();
            }
            setReady(true);
        }
        doInit();
    }, []);

    const handleCpopLoginComplete = async () => {
        const migrationStateNew = {...migrationState, loginPage: 'select'};
        migrationStateNew.processing = false;
        
        const cpopLoginJson = Cookies.get('cpop_login');
        if (!cpopLoginJson){
            migrationStateNew.page = 'login';
            setMigrationState(migrationStateNew);
        }
        const cpopLoginData = JSON.parse(cpopLoginJson);
        const customerData = await fetchCpopCustomer(cpopLoginData);
        if(!customerData){
            errorToast('Customer could not be found with the provided email address.');
            Cookies.remove('cpop_login')
            migrationStateNew.page = 'login';
            setMigrationState(migrationStateNew);
            return;
        }
        migrationStateNew.cpopUser = customerData;

        const bfxSessionData = getLoginCookie();
        if (bfxSessionData != null){
            const emailMatch = bfxSessionData.email == customerData.email;
            if (emailMatch){
                goToSubscriptionMigration(customerData);
                return;
            }
        }

        const existingSigninResult = await checkBfxLogin(customerData.email);
        const status = existingSigninResult.status;
        if (status == 'signup required' || status == 'no password'){
            migrationStateNew.page = 'signup'
        }
        else if (status == 'oauth required' || status == 'ok'){
            migrationStateNew.page = 'bfx-login'
        }
        setMigrationState(migrationStateNew);
    }

    const checkBfxLogin = async email => {
        return new Promise((resolve, reject) => {
            grecaptcha.ready(() => {
                grecaptcha.execute('6LeQ58wUAAAAAOO3JdYg_gxcIMwN9GXg8cWkz1i_', { action: 'login_email_check' })
                .then ((token) => {
                    const payload = {
                        'email' : email,
                        'g-recaptcha-response' : token
                    }
                    return axiosPost('/weblogin', payload)
                })
                .then((res) => {
                    resolve(res.data);
                })
                .catch(err => {
                    console.error(err);
                    reject();
                })
            })
        })
    }

    const handleSignupComplete = data => {
        setLoginCookie(data);
        queryClient.invalidateQueries({queryKey: ['cpop', 'customer']});
        goToSubscriptionMigration();
    }

    const handleLoginComplete = () => {
        queryClient.invalidateQueries({queryKey: ['cpop', 'customer']});
        _renderLoginBar();
        goToSubscriptionMigration();
    }

    const handleMigrationComplete = () => {
        setMigrationState({...migrationState, page: 'download'});
    }

    const goToSubscriptionMigration = async (cpopUser) => {
        const user = migrationState.cpopUser || cpopUser;
        const newPage = user.subscriptions.length > 0 ? 'subscription' : 'download';
        const migrationStateNew = {...migrationState, page: newPage, processing: false};
        const cust = customer.email != '' ? customer : await fetchCustomer();
        if (!migrationState.cpopUser){
            migrationStateNew.cpopUser = user;
        }
        if (newPage == 'subscription' && cust.gateway == null){
            await axiosPost('/customer/2/address', cust);
            queryClient.invalidateQueries({queryKey: ['cpop', 'customer']});
        }
        setMigrationState(migrationStateNew);
    }

    const fetchCpopCustomer = async data => {
        try{
            const result = await axiosGet('/cpop/account', {auth: {username: data.key, password: data.token}});
            return result.data;
        }
        catch(err){
            console.error(err);
            return null;
        }
    }

    if (migrationState.page == 'login'){
        content = (
            <div className='box cpop-login-panel-container'>
                <CrumplePopLoginPanel onComplete={handleCpopLoginComplete} />
            </div>
        )
    }
    else if (migrationState.page == 'signup'){
        content = <CrumplePopSignup onComplete={handleSignupComplete} />
    }
    else if (migrationState.page == 'bfx-login'){
        content = <BorisSignIn onComplete={handleLoginComplete} />
    }
    else if (migrationState.page == 'subscription'){
        const gatewayKey = customer ? getStripeKey(customer.gateway) : '';
        content = (
            <StripeProvider publicKey={gatewayKey}>
                <CrumplePopSubscriptionMigration customerInfo={customerInfo} onComplete={handleMigrationComplete} />
            </StripeProvider>
        )
    }
    else if (migrationState.page == 'download'){
        content = <CrumplePopMigrationResults />
    }

    const showContentSpinner = (migrationState.processing && migrationState.page != 'login');

    return (
        <div className='cpop-migration-content'>
            <SpinnerOverlay id="content-spinner" hidden={!showContentSpinner} size={96}>
            <SpinnerOverlay id="fullscreen-spinner" fullscreen opaque hidden={ready} message='Please Wait...' size={94} />
            <div className='d-flex justify-content-center'>
                <MigrationProgress steps={migrationSteps} activeStepName={migrationState.page} />
            </div>
            {migrationState.cpopUser && (
                <div>
                    <CrumplePopAccountDetails data={migrationState.cpopUser} />
                </div>
            )}
            <div className='w-100'>
                {content}
            </div>
            </SpinnerOverlay>
        </div>
    )
}

